import { Link } from "react-router-dom";
import { Network } from "lucide-react";
import { PageHeader, PageState, StatusPill, Empty } from "@/components/app/Common";
import { HealthGauge, ScoreChip } from "@/components/app/HealthGauge";
import { useLoad } from "@/hooks/useLoad";
import { api } from "@/lib/api";
import { bandColor, bandFor } from "@/lib/ui";
import { PortfolioInsights } from "@/components/app/PortfolioInsights";

const Node = ({ e, children, depth }) => (
  <li className="relative">
    <div className={`surface p-4 flex items-center gap-4 lift ${depth ? "ml-8" : ""}`} data-testid={`portfolio-node-${e.entity_id}`}
      style={{ borderLeft: `3px solid ${bandColor(e.band)}` }}>
      {depth > 0 && <span className="absolute -left-0 top-1/2 w-6 border-t border-border" style={{ left: `${depth * 32 - 26}px` }} />}
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{e.name}</div>
        <div className="text-xs text-muted-foreground">{e.jurisdiction} · {e.factors.length} issue(s)</div>
      </div>
      <StatusPill status={e.status} />
      <div className="text-right"><ScoreChip score={e.score} band={e.band} /><div className="text-[11px] text-muted-foreground">{e.band}</div></div>
    </div>
    {children?.length > 0 && <ul className="mt-3 space-y-3 border-l border-border ml-4 pl-0">{children}</ul>}
  </li>
);

const Tree = ({ ents }) => {
  const ids = new Set(ents.map((e) => e.entity_id));
  const render = (parent, depth) => ents.filter((e) => (parent ? e.parent_id === parent : !e.parent_id || !ids.has(e.parent_id)))
    .map((e) => <Node key={e.entity_id} e={e} depth={depth}>{render(e.entity_id, depth + 1)}</Node>);
  return <ul className="space-y-3" data-testid="portfolio-tree">{render(null, 0)}</ul>;
};

export default function Portfolio() {
  const state = useLoad(() => api.get("/portfolio").then((r) => r.data));
  const d = state.data;
  return (
    <div>
      <PageHeader eyebrow="Module 10" title="Multi-entity portfolio" subtitle="Holding company structure with individual scores, a group health score and portfolio risk." />
      {d?.has_entities && <PortfolioInsights />}
      <PageState state={state}>
        {d && (!d.has_entities ? <Empty icon={Network} title="No entities yet" text="Add entities and set their parent company to build your group structure." testId="portfolio-empty" /> : (
          <div className="grid lg:grid-cols-[340px_1fr] gap-6">
            <div className="space-y-4 min-w-0">
              <div className="surface p-6 flex flex-col items-center" data-testid="group-health-card">
                <HealthGauge score={d.score} band={d.band} size={220} label="Group Health" testId="group-health-gauge" />
                <div className="mt-4 text-sm">Portfolio risk: <span className="font-semibold" data-testid="portfolio-risk-label">{d.portfolio_risk}</span></div>
                <div className="text-xs text-muted-foreground">{d.entities_at_risk} of {d.entities.length} entities below 50</div>
              </div>
              <Link to="/app/entities" className="block text-sm text-primary" data-testid="portfolio-manage-link">Manage structure →</Link>
            </div>
            <div className="space-y-8 min-w-0">
              <Tree ents={d.entities} />
              <div className="surface overflow-x-auto" data-testid="portfolio-matrix">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b border-border"><tr>{["Entity", "Compliance", "Tax", "Governance", "Receipts", "Score"].map((h) => <th key={h} className="text-left font-medium px-4 py-3">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-border">
                    {d.entities.map((e) => (
                      <tr key={e.entity_id}>
                        <td className="px-4 py-3 font-medium">{e.name}</td>
                        {["compliance", "tax", "governance", "receipts"].map((k) => (
                          <td key={k} className="px-4 py-3"><span className="font-mono text-xs px-2 py-1 rounded" style={{ background: `${bandColor(bandFor(e.subscores[k]))}22`, color: bandColor(bandFor(e.subscores[k])) }}>{e.subscores[k]}</span></td>
                        ))}
                        <td className="px-4 py-3"><ScoreChip score={e.score} band={e.band} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </PageState>
    </div>
  );
}
